import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { CheckCircleOutline as CheckCircleOutlineIcon } from '@mui/icons-material';
import { Link as RouterLink, useOutletContext } from 'react-router-dom';
import api from '../../api';
import {
  MAINTENANCE_CATEGORIES,
  MAINTENANCE_URGENCIES,
  PORTAL_LINK_UNAVAILABLE,
  portalTokenErrorMessage,
} from './portalTokenMessages';

/**
 * ZRF-WTPB-009 — mobile-first maintenance intake via portal token.
 * Scope (property/unit/tenant) comes from the token on the server — never from form IDs.
 */
const PortalMaintenanceForm = () => {
  const { token, tokenHint, validation, validationLoading, validationError } =
    useOutletContext() || {};

  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('plumbing');
  const [description, setDescription] = useState('');
  const [location, setLocation] = useState('');
  const [urgency, setUrgency] = useState('medium');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [confirmation, setConfirmation] = useState(null);

  const scopeError =
    validation && validation.valid
      ? portalTokenErrorMessage(validation, { requiredActionType: 'submit_maintenance' })
      : null;

  const linkUnavailable =
    Boolean(validationError) ||
    (validation && validation.valid === false) ||
    Boolean(scopeError);

  const unavailableMessage =
    validationError ||
    (validation && validation.valid === false
      ? portalTokenErrorMessage(validation)
      : null) ||
    scopeError ||
    PORTAL_LINK_UNAVAILABLE;

  useEffect(() => {
    if (validation?.valid && validation.actionType === 'submit_maintenance') {
      console.log('🔍 portal maintenance token valid', {
        actionType: validation.actionType,
        tokenHint,
      });
    }
  }, [validation, tokenHint]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    if (!token || linkUnavailable) {
      setSubmitError(PORTAL_LINK_UNAVAILABLE);
      return;
    }
    if (!title.trim()) {
      setSubmitError('Please describe the issue in a short title.');
      return;
    }

    setSubmitting(true);
    try {
      // Do NOT send propertyId / unitId / tenantId — server stamps from token
      const res = await api.post(
        `/api/portal/tokens/${encodeURIComponent(token)}/maintenance`,
        {
          title: title.trim(),
          category,
          description: description.trim(),
          location: location.trim() || undefined,
          urgency,
        }
      );

      if (res.data?.valid === false) {
        setSubmitError(portalTokenErrorMessage(res.data) || PORTAL_LINK_UNAVAILABLE);
        return;
      }

      setConfirmation({
        maintenanceRequestId: res.data?.maintenanceRequestId,
        confirmationMessage:
          res.data?.confirmationMessage || 'Maintenance request has been created.',
        status: res.data?.status || 'submitted',
      });
      console.log('✅ portal maintenance submitted', {
        maintenanceRequestId: res.data?.maintenanceRequestId,
      });
    } catch (err) {
      console.error('❌ portal maintenance submit failed:', err);
      const data = err?.response?.data;
      setSubmitError(
        portalTokenErrorMessage(data) ||
          data?.error?.message ||
          data?.error ||
          'Could not submit your request. Please try again or ask your landlord for a new link.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (validationLoading) {
    return (
      <Stack alignItems="center" spacing={2} sx={{ py: 4 }}>
        <CircularProgress size={36} />
        <Typography variant="body2" color="text.secondary">
          Checking your secure link…
        </Typography>
      </Stack>
    );
  }

  if (linkUnavailable) {
    return (
      <Stack spacing={2.5}>
        <Typography variant="h5" component="h1">
          Link unavailable
        </Typography>
        <Alert severity="warning">{unavailableMessage}</Alert>
        <Typography variant="body2" color="text.secondary">
          For privacy, we do not show property or tenant details when a link cannot be used.
        </Typography>
      </Stack>
    );
  }

  if (confirmation) {
    return (
      <Stack spacing={2.5} alignItems="stretch">
        <Box sx={{ textAlign: 'center', pt: 1 }}>
          <CheckCircleOutlineIcon color="success" sx={{ fontSize: 48 }} />
        </Box>
        <Typography variant="h5" component="h1" textAlign="center">
          Request submitted
        </Typography>
        <Alert severity="success">{confirmation.confirmationMessage}</Alert>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          Your landlord can see this in ZenRentFlow. You can close this page.
        </Typography>
        {confirmation.maintenanceRequestId ? (
          <Typography variant="caption" color="text.secondary" textAlign="center">
            Reference {String(confirmation.maintenanceRequestId).slice(0, 8)}…
          </Typography>
        ) : null}
        <Button
          component={RouterLink}
          to={`/p/t/${encodeURIComponent(token || '')}`}
          variant="outlined"
          size="large"
          fullWidth
        >
          Back to link home
        </Button>
      </Stack>
    );
  }

  return (
    <Stack spacing={2.5} component="form" onSubmit={handleSubmit} noValidate>
      <Typography variant="h5" component="h1">
        Maintenance request
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Describe the issue. Property and unit are already set by this secure link — you do not
        enter IDs.
      </Typography>

      {submitError ? <Alert severity="error">{submitError}</Alert> : null}

      <TextField
        label="What is the issue?"
        fullWidth
        required
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Kitchen sink leak"
        inputProps={{ maxLength: 120 }}
        disabled={submitting}
      />
      <TextField
        select
        label="Category"
        fullWidth
        value={category}
        onChange={(e) => setCategory(e.target.value)}
        disabled={submitting}
      >
        {MAINTENANCE_CATEGORIES.map((c) => (
          <MenuItem key={c.value} value={c.value}>
            {c.label}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        label="Where is it? (optional)"
        fullWidth
        value={location}
        onChange={(e) => setLocation(e.target.value)}
        placeholder="Kitchen"
        disabled={submitting}
      />
      <TextField
        label="Describe the issue"
        fullWidth
        multiline
        minRows={3}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Water is leaking under the kitchen sink."
        disabled={submitting}
      />
      <TextField
        select
        label="Urgency"
        fullWidth
        value={urgency}
        onChange={(e) => setUrgency(e.target.value)}
        disabled={submitting}
      >
        {MAINTENANCE_URGENCIES.map((u) => (
          <MenuItem key={u.value} value={u.value}>
            {u.label}
          </MenuItem>
        ))}
      </TextField>

      <Button
        type="submit"
        variant="contained"
        size="large"
        fullWidth
        disabled={submitting}
        sx={{ py: 1.5 }}
      >
        {submitting ? <CircularProgress size={22} color="inherit" /> : 'Submit request'}
      </Button>
      <Button
        component={RouterLink}
        to={`/p/t/${encodeURIComponent(token || '')}`}
        size="small"
        fullWidth
        disabled={submitting}
      >
        Back to link home
      </Button>
    </Stack>
  );
};

export default PortalMaintenanceForm;
